// pg-trail: one dashed path through every `.pg-trail` waypoint on the page, in
// document order, drawn in a single overlay across all sections. Sections are
// separate posts, so a path drawn per section could not meet its neighbour
// (page 255); measuring the rendered waypoints here can.
//
// The line is masked out of the page's content (text, buttons, cards, photos),
// so it reads as passing behind them, and is drawn in a second colour over deep
// grounds. With `data-mark="paw"` on any waypoint, paw prints walk the path,
// facing the direction of travel, and fade in as they scroll into view.

const SVG = "http://www.w3.org/2000/svg";

const CONTENT =
  "h1,h2,h3,h4,h5,h6,p,li,dt,dd,blockquote,figure,img,video,iframe,a,button,input,select,textarea,label,table," +
  ".pg-card,.pg-btn,.pg-media,.pg-quote,.pg-notice,.pg-cta,.pg-person,.pg-stats,.pg-field,.pg-form";

const DEEP = ".pg-band--deep,.pg-band--primary,.pg-on-deep";

// Toes up: rotated by the path's heading + 90°.
const PAW =
  '<ellipse cx="6" cy="7" rx="2" ry="2.8"/><ellipse cx="10.3" cy="4" rx="2" ry="2.8"/>' +
  '<ellipse cx="14.7" cy="4" rx="2" ry="2.8"/><ellipse cx="19" cy="7" rx="2" ry="2.8"/>' +
  '<path d="M7.5 14C6.5 16 8 20.5 12.5 20.5S18.5 16 17.5 14C16 11.5 14 11.5 12.5 13 11 11.5 9 11.5 7.5 14Z"/>';

const HOOKS = ["--pg-trail-c", "--pg-trail-c-deep", "--pg-trail-w", "--pg-trail-dash"];

const LIGHT = "var(--pg-trail-c,var(--brand-primary))";
const ON_DEEP = "var(--pg-trail-c-deep,var(--brand-accent))";
const MARK_OPACITY = 0.9;

const line = (colour, opacity) =>
  `fill:none;stroke:${colour};stroke-width:var(--pg-trail-w,3px);stroke-dasharray:var(--pg-trail-dash,10 10);stroke-linecap:round;opacity:${opacity}`;

const PAD = 10; // clearance kept around content
const STEP = 70; // px of path between prints
const MARK = 28; // print size, px

const el = (name, attrs = {}) => {
  const node = document.createElementNS(SVG, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
};

// Catmull-Rom through the points, as cubic Béziers.
const smoothPath = (pts) => {
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [p0, p1, p2, p3] = [pts[i - 1] || pts[i], pts[i], pts[i + 1], pts[i + 2] || pts[i + 1]];
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    d += ` C${c1.x},${c1.y} ${c2.x},${c2.y} ${p2.x},${p2.y}`;
  }
  return d;
};

// Layout boxes relative to the container, from the offset chain rather than
// getBoundingClientRect: AOS holds content translated until it animates in,
// and a box measured mid-entrance would mask the wrong place.
const box = (node, root) => {
  let x = 0;
  let y = 0;
  for (let n = node; n !== root; n = n.offsetParent) {
    if (!n) return null;
    x += n.offsetLeft;
    y += n.offsetTop;
  }
  return { x, y, w: node.offsetWidth, h: node.offsetHeight };
};

const rectsIn = (root, selector, pad) =>
  [...root.querySelectorAll(selector)]
    .filter((n) => !n.closest(".pg-decor,.pg-trail-layer"))
    .map((n) => box(n, root))
    .filter((r) => r && r.w && r.h)
    .map((r) => ({ x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 }));

// Boxed surfaces a section built with its own classes (cards, panels,
// banners): anything narrower than a band that paints a background or a
// shadow. Bands and section roots are full-width and stay open to the line.
const surfaces = (root) => {
  const wide = root.offsetWidth * 0.8;
  return [...root.querySelectorAll(".pg *")]
    .filter((n) => n.offsetWidth && n.offsetWidth < wide && !n.closest(".pg-decor,.pg-trail-layer"))
    .filter((n) => {
      const s = getComputedStyle(n);
      return s.boxShadow !== "none" || !/rgba\(.*,\s*0\)|transparent/.test(s.backgroundColor);
    })
    .map((n) => box(n, root))
    .filter(Boolean)
    .map((r) => ({ x: r.x - PAD, y: r.y - PAD, w: r.w + PAD * 2, h: r.h + PAD * 2 }));
};

const inside =(p, r) => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;

const maskOf = (id, keep, cut, size) => {
  const mask = el("mask", { id, maskUnits: "userSpaceOnUse", x: 0, y: 0, width: size.w, height: size.h });
  if (keep) keep.forEach((r) => mask.append(el("rect", { x: r.x, y: r.y, width: r.w, height: r.h, fill: "#fff" })));
  else mask.append(el("rect", { x: 0, y: 0, width: size.w, height: size.h, fill: "#fff" }));
  cut.forEach((r) => mask.append(el("rect", { x: r.x, y: r.y, width: r.w, height: r.h, fill: "#000" })));
  return mask;
};

let uid = 0;

function draw(container) {
  container.querySelector(":scope > .pg-trail-layer")?.remove();
  container._trailObserver?.disconnect();

  const points = [...container.querySelectorAll(".pg-trail")];
  if (points.length < 2) return;

  // Waypoints and content are measured against the container.
  if (getComputedStyle(container).position === "static") container.style.position = "relative";
  const size = { w: container.offsetWidth, h: container.offsetHeight };
  const pts = points.map((p) => box(p, container)).filter(Boolean);
  if (pts.length < 2) return;

  const content = [...rectsIn(container, CONTENT, PAD), ...surfaces(container)];
  const deep = rectsIn(container, DEEP, 0);
  const id = `pg-trail-${++uid}`;
  const d = smoothPath(pts);

  const svg = el("svg", {
    class: "pg-trail-layer",
    "aria-hidden": "true",
    viewBox: `0 0 ${size.w} ${size.h}`,
    style: "position:absolute;inset:0;width:100%;height:100%;z-index:1;pointer-events:none;overflow:visible",
  });
  // The layer sits beside the sections, not in them, so hooks a section sets
  // reach it through the first waypoint.
  const cs = getComputedStyle(points[0]);
  for (const hook of HOOKS) {
    const v = cs.getPropertyValue(hook).trim();
    if (v) svg.style.setProperty(hook, v);
  }

  const defs = el("defs");
  defs.append(maskOf(`${id}-light`, null, [...deep, ...content], size));
  defs.append(maskOf(`${id}-deep`, deep, content, size));
  svg.append(defs);
  svg.append(el("path", { d, mask: `url(#${id}-light)`, style: line(LIGHT, 0.6) }));
  if (deep.length) svg.append(el("path", { d, mask: `url(#${id}-deep)`, style: line(ON_DEEP, 0.75) }));
  container.append(svg);

  if (!points.some((p) => p.dataset.mark === "paw")) return;

  const path = svg.querySelector("path");
  const length = path.getTotalLength();
  const still = matchMedia("(prefers-reduced-motion: reduce)").matches || container.dataset.motion === "off";
  const seen = (container._trailSeen ||= new Set());
  const marks = [];

  for (let s = STEP / 2, i = 0; s < length; s += STEP, i++) {
    const a = path.getPointAtLength(s);
    const b = path.getPointAtLength(Math.min(s + 1, length));
    const heading = Math.atan2(b.y - a.y, b.x - a.x);
    // Left and right feet, a little either side of the line.
    const side = i % 2 ? 9 : -9;
    const p = { x: a.x - Math.sin(heading) * side, y: a.y + Math.cos(heading) * side };
    if (content.some((r) => inside(p, r))) continue;

    const deg = (heading * 180) / Math.PI + 90;
    const shown = still || seen.has(i);
    const g = el("g", {
      transform: `translate(${p.x - MARK / 2},${p.y - MARK / 2}) rotate(${deg} ${MARK / 2} ${MARK / 2}) scale(${MARK / 24})`,
      fill: "currentColor",
      style: `color:${deep.some((r) => inside(p, r)) ? ON_DEEP : LIGHT};opacity:${shown ? MARK_OPACITY : 0};transition:opacity .6s ease-out`,
    });
    g.innerHTML = PAW;
    g.dataset.i = i;
    svg.append(g);
    if (!shown) marks.push(g);
  }

  // Prints arriving together step in one after another. A redraw (resize,
  // late images) keeps the ones already shown rather than fading them again.
  const io = new IntersectionObserver(
    (entries) =>
      entries
        .filter((e) => e.isIntersecting)
        .forEach((e, n) => {
          e.target.style.transitionDelay = `${n * 90}ms`;
          e.target.style.opacity = MARK_OPACITY;
          seen.add(Number(e.target.dataset.i));
          io.unobserve(e.target);
        }),
    { rootMargin: "0px 0px -10% 0px" },
  );
  marks.forEach((m) => io.observe(m));
  container._trailObserver = io;
}
function watch(container) {
  if (container._trail) return;
  container._trail = true;

  let queued = false;
  const redraw = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      draw(container);
    });
  };

  new ResizeObserver(redraw).observe(container);
  // The editor preview re-renders sections in place. Our own layer, and
  // anything inside it, is ignored; attribute changes are not watched at all,
  // since AOS and the prints' own fade-in toggle classes on every scroll.
  // A LiveView patch arrives as a burst of mutations, and each draw reads the
  // computed style of every element, so the redraw waits for the burst to end.
  const ours = (n) => n.classList?.contains("pg-trail-layer") || n.closest?.(".pg-trail-layer");
  let settle;
  new MutationObserver((records) => {
    if (records.some((r) => !ours(r.target) && ![...r.addedNodes, ...r.removedNodes].every(ours))) {
      clearTimeout(settle);
      settle = setTimeout(redraw, 150);
    }
  }).observe(container, { childList: true, subtree: true });
  window.addEventListener("load", redraw, { once: true });
  redraw();
}

const scan = () => document.querySelectorAll(".ai-page-content").forEach(watch);

scan();
window.addEventListener("phx:page-loading-stop", scan);
